import { Injectable } from "@angular/core";
import { BehaviorSubject, AsyncSubject } from "rxjs";
import { MEError } from "../../error/me-error";
import { MEAPIResponse, MEEntityAPIResponseHandler, MEDoNothingResponseHandler, ResponseCode } from "../api/me-api-response";
import { MEAPIService } from "../api/me-api.service";
import { InternalSettings, Permissions } from "./internal-settings.interface";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService, LocalStorageType } from "src/app/system/utility/local-storage.service";
import { FormatUtility } from "../../components/me-validation/me-format-validation";


@Injectable({
  providedIn: 'root'
})
export class InternalSettingsService {
  private readonly API_INTERNAL_SETTINGS : string = MEAPIService.BASE_URL + '/customer/internalSetting';

  public internalSettings$ : BehaviorSubject<InternalSettings | null> = new BehaviorSubject<InternalSettings | null>(null);
  
  constructor(private srvApi : MEAPIService, 
              private translate: TranslateService, 
              private localStorageService: LocalStorageService) {}

  // Lookup existing record without network call, trigger network call in case empty
  getCurrentSettings() : AsyncSubject<InternalSettings> {
    let ret = new AsyncSubject<InternalSettings>();

    this.internalSettings$.subscribe((is) => {
      if (is) {
        ret.next(is);
        ret.complete();
      }
    })
    
    return ret;
  }

  // Trigger the Actual Network call if necessary, by refreshing the internal state
  loadSettings() : AsyncSubject<boolean> {
    return this.srvApi.Get(this.API_INTERNAL_SETTINGS, [
      new SuccessfulInternalSettingsHandler(this),
      new MEDoNothingResponseHandler(ResponseCode.USER_FORCED_CHANGE_PASSWORD),
    ]);
  }

  handleSuccess(response : MEAPIResponse<InternalSettings>) {
    let is: InternalSettings = response.entity;

    // Update User Language
    let language = is.profile.language + ((is.bankOrCreditUnion === 'creditUnion') ? '-cu' : '');
    this.translate.use(language);
    this.localStorageService.set(LocalStorageType.LANGUAGE, language);
    FormatUtility.init(this.translate, language);

    // Forward to Subscriber
    this.internalSettings$.next(is);

  }
}


export function hasAllPermissions(requiredPermissions : number[], userPermissions : Permissions[] ) : boolean {

  return requiredPermissions.every((p) => {
    return userPermissions.find((up : Permissions ) => {
      return up.code == p;
    });
  })
}

export function hasOnePermission(requiredPermissions: number[], userPermissions : Permissions[] ) : boolean {

  return requiredPermissions.some((p) => {
    return userPermissions.find((up : Permissions ) => {
      return up.code == p;
    });
  })
}

export function hasPermission(permission: number, userPermissions: Permissions[]): boolean {
  return userPermissions.some((up: Permissions) => {
    return up.code == permission;
  });
}

class SuccessfulInternalSettingsHandler implements MEEntityAPIResponseHandler<InternalSettings> {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private srvIS : InternalSettingsService) {}

  handle(response: MEAPIResponse<InternalSettings>): boolean | MEError {
    this.srvIS.handleSuccess(response);
    return true;
  }
}
